import { resolveTaskName } from '../../utils/utils';
import type { GanttVisibleOptionalColumns } from '../../features/schedule-tasks/components/GanttGridColumnToggles';
import { TASK_TYPE_OPTIONS } from './taskTypeOptions';
import type { GanttColumnDef } from './types';

/**
 * Ordered column metadata for filters / labels.
 * Visibility of optional columns still comes from GanttVisibleOptionalColumns.
 */
export const SCHEDULE_COLUMN_DEFS: GanttColumnDef[] = [
  {
    id: 'srNo',
    label: 'Sr No',
    filterCaption: 'Enter an exact Sr No.',
    alwaysVisible: true,
    filter: {
      type: 'text',
      getValue: (task) => (task.srNo != null ? String(task.srNo).trim() : ''),
    },
  },
  {
    id: 'text',
    label: 'Task name',
    filterCaption: 'Match tasks whose name contains the text you enter.',
    alwaysVisible: true,
    filter: {
      type: 'text',
      getValue: (task) => resolveTaskName(task),
    },
  },
  {
    id: 'start_date',
    label: 'Start',
    filterCaption: 'Keep tasks whose planned start falls within this date range.',
    alwaysVisible: true,
    filter: {
      type: 'dateRange',
      getValue: (task) => task.start ?? null,
    },
  },
  {
    id: 'end_date',
    label: 'End',
    filterCaption: 'Keep tasks whose planned finish falls within this date range.',
    alwaysVisible: true,
    filter: {
      type: 'dateRange',
      getValue: (task) => task.finish ?? null,
    },
  },
  {
    id: 'duration',
    label: 'Duration',
    filterCaption: 'Filter by planned duration in working days (min and/or max).',
    optionalKey: 'duration',
    filter: {
      type: 'numberRange',
      getValue: (task) => task.duration ?? null,
    },
  },
  {
    id: 'progress',
    label: 'Progress',
    filterCaption: 'Filter by percent complete (0–100).',
    alwaysVisible: true,
    filter: {
      type: 'numberRange',
      getValue: (task, live) => {
        if (live?.progress != null && Number.isFinite(Number(live.progress))) {
          return Math.round(Number(live.progress) * 100);
        }
        const pct = task.percentComplete;
        return pct == null ? null : Number(pct);
      },
    },
  },
  {
    id: 'critical',
    label: 'Critical',
    filterCaption: 'Show only tasks that are on the critical path, or only those that are not.',
    optionalKey: 'critical',
    filter: {
      type: 'boolean',
      // live.critical is MSP∪DHTMLX (see getLiveMeta); fall back to snapshot alone.
      getValue: (task, live) => {
        if (typeof live?.critical === 'boolean') return live.critical;
        return !!task.critical;
      },
    },
  },
  {
    id: 'taskType',
    label: 'Task type',
    filterCaption: 'Select one or more planning types to keep in the grid.',
    optionalKey: 'taskType',
    filter: {
      type: 'select',
      options: TASK_TYPE_OPTIONS.map((o) => ({ value: o.key, label: o.label })),
      getValue: (task) => String(task.taskType ?? '').trim() || 'unknown',
    },
  },
  {
    id: 'locationType',
    label: 'Location name',
    filterCaption: 'Match tasks by location display name (partial text).',
    optionalKey: 'locationType',
    filter: {
      type: 'text',
      getValue: (task) => task.locationType ?? '',
    },
  },
  {
    id: 'predecessors',
    label: 'Predecessor',
    filterCaption: 'Enter an exact predecessor Sr No to show that task’s dependents.',
    optionalKey: 'predecessors',
    filter: {
      type: 'text',
      // Placeholder — collector matches exact predecessor Sr No via context.
      getValue: () => '',
    },
  },
  {
    id: 'unitActivityTracked',
    label: 'Tracked',
    filterCaption: 'Show leaf tasks that are tracked as unit activities, or those that are not.',
    optionalKey: 'tracked',
    filter: {
      type: 'boolean',
      getValue: (task, live) => {
        if (typeof live?.tracked === 'boolean') return live.tracked;
        const details = task.unitActivityDetails;
        return Array.isArray(details) && details.length > 0;
      },
    },
  },
  {
    id: 'assignees',
    label: 'Assignees',
    filterCaption: 'Select one or more people. Tasks with any of them assigned are shown.',
    optionalKey: 'assignees',
    filter: {
      type: 'select',
      options: [], // Populated at runtime from live assignments
      getValue: () => '',
    },
  },
];

/** Column defs that should show a filter control given current optional visibility. */
export function getFilterableColumnDefs(
  visibleOptionalColumns: GanttVisibleOptionalColumns,
): GanttColumnDef[] {
  return SCHEDULE_COLUMN_DEFS.filter((def) => {
    if (!def.filter) return false;
    if (def.alwaysVisible) return true;
    if (def.optionalKey) return !!visibleOptionalColumns[def.optionalKey];
    return true;
  });
}
